import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Upload } from "lucide-react";
import { toast } from "sonner";
import { http, errMsg, coverSrc, STORE_LABELS } from "../lib/api";
import { PageLoading } from "../context/AuthContext";
import { BookCover } from "../components/Bits";
import TagInput from "../components/TagInput";

const EMPTY = { title: "", subtitle: "", genre: "", synopsis: "", target_reader: "", comps: [], moods: [], themes: [], price: "", formats: [], launch_date: "", author_notes: "", buy_links: {} };
const STEPS = ["Basics", "Story & tribe", "Cover & buy links"];
const FORMATS = ["Ebook", "Paperback", "Hardcover", "Audiobook"];
const MOODS = ["cozy", "dark", "hopeful", "romantic", "tense", "funny", "atmospheric", "slow-burn"];
const THEMES = ["found family", "dark academia", "enemies to lovers", "small town", "grief", "coming of age"];
const GENRES = ["Fantasy", "Romance", "Mystery", "Thriller", "Science Fiction", "Literary Fiction", "Horror", "Historical Fiction", "Young Adult", "Memoir", "Self-help", "Poetry"];

const Field = ({ label, hint, children }) => (
  <label className="block">
    <span className="label-mono">{label}</span>
    {hint && <span className="block text-sm text-[#8E8879] mt-1">{hint}</span>}
    <div className="mt-2.5">{children}</div>
  </label>
);

function StepBasics({ f, set }) {
  return (
    <div className="grid sm:grid-cols-2 gap-7">
      <div className="sm:col-span-2"><Field label="Title *"><input className="input-paper font-display text-2xl" value={f.title} onChange={(e) => set("title", e.target.value)} placeholder="The name on the spine" data-testid="book-form-title-input" /></Field></div>
      <div className="sm:col-span-2"><Field label="Subtitle"><input className="input-paper" value={f.subtitle} onChange={(e) => set("subtitle", e.target.value)} placeholder="Optional" data-testid="book-form-subtitle-input" /></Field></div>
      <Field label="Genre">
        <input className="input-paper" list="genres" value={f.genre} onChange={(e) => set("genre", e.target.value)} placeholder="e.g. Fantasy" data-testid="book-form-genre-input" />
        <datalist id="genres">{GENRES.map((g) => <option key={g} value={g} />)}</datalist>
      </Field>
      <Field label="Launch date"><input type="date" className="input-paper" value={f.launch_date} onChange={(e) => set("launch_date", e.target.value)} data-testid="book-form-launch-date-input" /></Field>
      <Field label="Price"><input className="input-paper" value={f.price} onChange={(e) => set("price", e.target.value)} placeholder="e.g. $4.99 ebook / $14.99 paperback" data-testid="book-form-price-input" /></Field>
      <Field label="Formats">
        <div className="flex flex-wrap gap-2.5 pt-1">
          {FORMATS.map((x) => {
            const on = f.formats.includes(x);
            return <button type="button" key={x} className={`stamp ${on ? "stamp-active" : ""}`} onClick={() => set("formats", on ? f.formats.filter((y) => y !== x) : [...f.formats, x])} data-testid={`book-form-format-${x.toLowerCase()}`}>{x}</button>;
          })}
        </div>
      </Field>
    </div>
  );
}

function StepStory({ f, set }) {
  return (
    <div className="space-y-7">
      <Field label="Synopsis" hint="What happens, in your own words. Rough is fine — we polish it."><textarea rows={6} className="input-paper" value={f.synopsis} onChange={(e) => set("synopsis", e.target.value)} data-testid="book-form-synopsis-input" /></Field>
      <Field label="Who is it for?"><input className="input-paper" value={f.target_reader} onChange={(e) => set("target_reader", e.target.value)} placeholder="e.g. adult readers who loved The Night Circus and want slow, lush magic" data-testid="book-form-target-reader-input" /></Field>
      <Field label="Comparable books or authors" hint="Press Enter after each."><TagInput value={f.comps} onChange={(v) => set("comps", v)} placeholder="e.g. Naomi Novik" testid="book-form-comps" /></Field>
      <Field label="Moods"><TagInput value={f.moods} onChange={(v) => set("moods", v)} placeholder="How does it feel?" suggestions={MOODS} testid="book-form-moods" /></Field>
      <Field label="Themes & tropes"><TagInput value={f.themes} onChange={(v) => set("themes", v)} placeholder="What is it about underneath?" suggestions={THEMES} testid="book-form-themes" /></Field>
      <Field label="About you" hint="A few true facts for your author bio. We only use what you tell us."><textarea rows={3} className="input-paper" value={f.author_notes} onChange={(e) => set("author_notes", e.target.value)} placeholder="Where you live, what you did before writing, what you love…" data-testid="book-form-author-notes-input" /></Field>
    </div>
  );
}

function StepCover({ f, set, file, setFile }) {
  const preview = file ? URL.createObjectURL(file) : coverSrc(f.cover_url);
  return (
    <div className="grid md:grid-cols-12 gap-10">
      <div className="md:col-span-4">
        <span className="label-mono">Cover</span>
        <BookCover src={preview} title={f.title || "Untitled"} className="mt-3 w-2/3 md:w-full" testid="book-form-cover-preview" />
        <label className="btn-secondary mt-5 cursor-pointer w-full" data-testid="book-form-cover-upload-label">
          <Upload size={13} /> {preview ? "Replace cover" : "Upload cover"}
          <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => setFile(e.target.files[0])} data-testid="book-form-cover-input" />
        </label>
        <p className="text-xs text-[#8E8879] mt-2">JPG, PNG or WEBP, under 3 MB. No cover? We set a typographic one.</p>
      </div>
      <div className="md:col-span-8 space-y-5">
        <span className="label-mono">Where readers can buy it</span>
        {Object.entries(STORE_LABELS).map(([k, label]) => (
          <div key={k} className="grid grid-cols-[8.5rem_1fr] items-center gap-3">
            <span className="font-display text-lg">{label}</span>
            <input className="input-paper" type="url" placeholder="https://…" value={f.buy_links[k] || ""} onChange={(e) => set("buy_links", { ...f.buy_links, [k]: e.target.value })} data-testid={`book-form-buy-${k}-input`} />
          </div>
        ))}
      </div>
    </div>
  );
}

export default function BookForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [f, setF] = useState(id ? null : EMPTY);
  const [step, setStep] = useState(0);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (id) http.get(`/books/${id}`).then(({ data }) => setF(data)).catch(() => navigate("/dashboard"));
  }, [id, navigate]);

  if (!f) return <PageLoading />;
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (!f.title.trim()) { setStep(0); return toast.error("Your book needs a title"); }
    setBusy(true);
    try {
      const payload = Object.fromEntries(Object.keys(EMPTY).map((k) => [k, f[k]]));
      const { data } = id ? await http.put(`/books/${id}`, payload) : await http.post("/books", payload);
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        await http.post(`/books/${data.id}/cover`, fd);
      }
      toast.success(id ? "Book updated" : "Book added to your desk");
      navigate(`/books/${data.id}`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-6 lg:px-10 pt-14" data-testid="book-form-page">
      <p className="label-mono">{id ? "Edit book" : "New book"}</p>
      <h1 className="font-display text-4xl sm:text-5xl tracking-tight mt-2">{id ? f.title : "Tell us about your book."}</h1>
      <ol className="flex gap-2 sm:gap-6 mt-10 border-b border-[#E5DEC9]" data-testid="book-form-stepper">
        {STEPS.map((s, i) => (
          <li key={s}>
            <button type="button" onClick={() => setStep(i)} className={`pb-3 -mb-px border-b-2 font-type text-[0.68rem] uppercase tracking-wider transition-colors ${step === i ? "border-[#C84B31] text-[#121210]" : "border-transparent text-[#8E8879] hover:text-[#121210]"}`} data-testid={`book-form-step-${i + 1}-tab`}>
              <span className="text-[#C84B31]">0{i + 1}</span> {s}
            </button>
          </li>
        ))}
      </ol>
      <div className="py-10 rise" key={step}>
        {step === 0 && <StepBasics f={f} set={set} />}
        {step === 1 && <StepStory f={f} set={set} />}
        {step === 2 && <StepCover f={f} set={set} file={file} setFile={setFile} />}
      </div>
      <div className="flex justify-between border-t border-[#E5DEC9] pt-6">
        <button className="btn-ghost" disabled={step === 0} onClick={() => setStep(step - 1)} data-testid="book-form-back-button"><ArrowLeft size={13} /> Back</button>
        {step < 2 ? (
          <button className="btn-primary" onClick={() => setStep(step + 1)} data-testid={`book-form-step-${step + 1}-next`}>Next <ArrowRight size={13} /></button>
        ) : (
          <button className="btn-primary" onClick={save} disabled={busy} data-testid="book-form-save-button">{busy ? "Saving…" : id ? "Save changes" : "Save & open launch kit"}</button>
        )}
      </div>
    </div>
  );
}
